import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useSearchParams, useNavigate } from "react-router";
import { Button, Container } from "react-bootstrap";
import {
  useSpinner,
  Spinner,
  getSubstring,
  getConfirmationModal,
  handleAsyncAction,
} from "../../../../shared";
import logo from "../../../../assets/images/hunger-rush-logo.webp";
import { changeSurveyPreferencesActions } from "../../redux/actions";
import { emailVerificationActions } from "../../../../redux/actions";
import {
  changeSurveyPreferencesConstants,
  responseType,
  paramsConstants,
  DEFAULT_SURVEY_PREFERENCE_CHECKBOX_STATE,
  BUTTON_TEXT,
} from "../../constants";
import { generateImagePath } from "../../../../shared/components/sidebarContainer/utility";
import PreferenceCheckboxRow from "./shared/preferenceCheckboxRow";
import {
  createCheckboxStateFromPreferences,
  createTurnedOffCheckboxState,
  togglePreferenceCheckbox,
  isSurveyPreferenceUpdateDisabled,
} from "./utils/surveyPreferenceCheckboxHelpers";

const {
  POSITIVE_RESPONSES,
  PASSIVE_RESPONSES,
  NEGATIVE_RESPONSES,
  TURN_OFF_RESPONSES,
  IS_POSITIVE_RESPONSE,
  IS_PASSIVE_RESPONSE,
  IS_NEGATIVE_RESPONSE,
  IS_TURN_OFF_RESPONSE,
  GET_SURVEY_PREFERENCES,
  CONFIRMATION_MODAL_NOTE,
  CHANGE_SURVEY_PREFERENCES,
  CONFIRMATION_MODAL_MESSAGE,
  EMAIL_NOTIFICATION_DESCRIPTION,
  LINK_EXPIRED_MESSAGE,
  NEGATIVE_RESPONSE_ID_KEY,
  POSITIVE_RESPONSE_ID_KEY,
  PASSIVE_RESPONSE_ID_KEY,
} = changeSurveyPreferencesConstants;
const { ENCRYPTED_PARAMETERS, ENCRYPTED_PARAMETERS_EQUALS } = paramsConstants;

const ChangeSurveyPreferences = (props) => {
  const navigate = useNavigate();

  const {
    changeSurveyPreferencesReducer: { surveyPreferences },
    confirmEmail,
    getSurveyPreferences,
    updateSurveyPreferences,
  } = props;

  const [encryptedParams] = useSearchParams();

  const [checkboxState, setCheckboxState] = useState(
    DEFAULT_SURVEY_PREFERENCE_CHECKBOX_STATE,
  );
  const [modalElement, setModalElement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const encryptedBody = encryptedParams.get(ENCRYPTED_PARAMETERS);

  // Show loading while a request is running.
  const spinner = useSpinner();

  const handleRedirection = (url) => {
    if (url.includes(ENCRYPTED_PARAMETERS)) {
      const encryptedParamsValue = getSubstring(
        url,
        ENCRYPTED_PARAMETERS_EQUALS,
      );
      if (encryptedParamsValue) {
        confirmEmail(encryptedParamsValue, navigate);
      }
    }
    navigate(surveyPreferences.accessDashboardLink, {
      replace: true,
    });
  };

  const handleCheckboxChange = (name) => {
    setCheckboxState((prevState) => togglePreferenceCheckbox(prevState, name));
  };

  const handleTurnOffResponseChange = () => {
    setCheckboxState((prevState) =>
      createTurnedOffCheckboxState(prevState.isTurnOffResponse),
    );
  };

  //To open the modal
  const openModal = () => {
    setShowModal(true);
  };

  //To close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  //Setting modal component
  const setComponent = (component) => {
    setModalElement(component);
    openModal();
  };

  const showConfirmationModal = () => {
    setComponent(
      getConfirmationModal({
        onClose: closeModal,
        Title: BUTTON_TEXT.CONFIRM,
        message: CONFIRMATION_MODAL_MESSAGE,
        note: CONFIRMATION_MODAL_NOTE,
        ModalSize: "md",
        onConfirm: () => {
          handleUpdateSurveyPreferences(true);
        },
      }),
    );
  };

  const handleUpdateSurveyPreferences = (isFromModal) => {
    const requestBody = {
      userId: surveyPreferences?.userId,
      surveyId: surveyPreferences?.surveyId,
      [POSITIVE_RESPONSE_ID_KEY]: checkboxState.isPositiveResponse
        ? responseType.POSITIVE
        : null,
      [NEGATIVE_RESPONSE_ID_KEY]: checkboxState.isNegativeResponse
        ? responseType.NEGATIVE
        : null,
      [PASSIVE_RESPONSE_ID_KEY]: checkboxState.isPassiveResponse
        ? responseType.PASSIVE
        : null,
    };
    handleAsyncAction(updateSurveyPreferences(encryptedBody, requestBody), {
      onSuccess: isFromModal ? closeModal : undefined,
    });
  };

  // Dedicated Update button handler (confirm turn-off vs direct update)
  const handleUpdateClick = () => {
    if (checkboxState.isTurnOffResponse) {
      showConfirmationModal();
    } else {
      handleUpdateSurveyPreferences();
    }
  };

  useEffect(() => {
    getSurveyPreferences(encryptedBody);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setCheckboxState(createCheckboxStateFromPreferences(surveyPreferences));
  }, [surveyPreferences]);

  return (
    <div>
      {/*Modal here */}
      {showModal && modalElement}
      {spinner(GET_SURVEY_PREFERENCES) ? (
        <Spinner />
      ) : (
        <Container
          fluid
          className="d-flex align-items-center justify-content-center background-height"
        >
          <Container className="col-lg-6 col-xl-5 rounded pb-3 pt-2 my-auto shadow">
            <div className="col-12 flex-row d-flex align-items-center justify-content-center">
              <img
                src={generateImagePath(props.imagePathFromHub, logo)}
                alt="logo"
                width={65}
                height="auto"
              />
              <span className="login-platform-name hunger-logo-color fs-xxl ml-n9">
                Hunger
              </span>
              <span className="login-platform-name login-platform-secondName-color fs-xxl">
                Rush
                <sup className="logo-superscript">TM</sup>
              </span>
            </div>
            {surveyPreferences ? (
              <div className="text-center px-4">
                <h5 className="fw-bold mb-0">{CHANGE_SURVEY_PREFERENCES}</h5>
                <p className="text-muted">{surveyPreferences?.email}</p>
                <p className="pt-3 fs-13">{EMAIL_NOTIFICATION_DESCRIPTION}</p>

                <PreferenceCheckboxRow
                  label={POSITIVE_RESPONSES}
                  name={IS_POSITIVE_RESPONSE}
                  checked={checkboxState.isPositiveResponse}
                  onChange={() => handleCheckboxChange(IS_POSITIVE_RESPONSE)}
                />
                <PreferenceCheckboxRow
                  label={PASSIVE_RESPONSES}
                  name={IS_PASSIVE_RESPONSE}
                  checked={checkboxState.isPassiveResponse}
                  onChange={() => handleCheckboxChange(IS_PASSIVE_RESPONSE)}
                />
                <PreferenceCheckboxRow
                  label={NEGATIVE_RESPONSES}
                  name={IS_NEGATIVE_RESPONSE}
                  checked={checkboxState.isNegativeResponse}
                  onChange={() => handleCheckboxChange(IS_NEGATIVE_RESPONSE)}
                />
                <PreferenceCheckboxRow
                  label={TURN_OFF_RESPONSES}
                  name={IS_TURN_OFF_RESPONSE}
                  checked={checkboxState.isTurnOffResponse}
                  onChange={handleTurnOffResponseChange}
                />
                <div className="d-flex flex-wrap align-items-center justify-content-center">
                  <div className="col-12 col-xl-5 col-md-6 px-4 py-1 py-md-4">
                    {surveyPreferences?.accessDashboardLink && (
                      <Button
                        type="button"
                        className="access-dashboard-btn w-100"
                        onClick={() => {
                          handleRedirection(
                            surveyPreferences.accessDashboardLink,
                          );
                        }}
                      >
                        Access Dashboard
                      </Button>
                    )}
                  </div>
                  <div className="col-12 col-xl-5 col-md-6 px-4 py-1 py-md-4">
                    <Button
                      type="button"
                      className="update-preference-btn w-100"
                      onClick={handleUpdateClick}
                      disabled={isSurveyPreferenceUpdateDisabled(checkboxState)}
                    >
                      {BUTTON_TEXT.UPDATE}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center px-4 my-4 py-4">
                <h6 className="mb-0">{LINK_EXPIRED_MESSAGE}</h6>
              </div>
            )}
          </Container>
        </Container>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  imagePathFromHub: state.system.fromHub?.imagePath,
  changeSurveyPreferencesReducer: state.changeSurveyPreferencesReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getSurveyPreferences: (encryptedBody) =>
    dispatch(
      changeSurveyPreferencesActions.getSurveyPreferences(encryptedBody),
    ),
  updateSurveyPreferences: (encryptedBody, requestBody) =>
    dispatch(
      changeSurveyPreferencesActions.updateSurveyPreferences(
        encryptedBody,
        requestBody,
      ),
    ),
  confirmEmail: (token, navigate) =>
    dispatch(emailVerificationActions.confirmEmail(token, navigate)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChangeSurveyPreferences);
